import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { BoletoEligibilityResponse, BoletoExistsResponse } from '../services/api/boleto.service';
import BoletoConfirmationDialog from './BoletoConfirmationDialog';

interface BoletoButtonProps {
  invoiceId: string;
  className?: string;
  style?: React.CSSProperties;
  onBoletoCreated?: (boletoData: any) => void;
  onError?: (error: string) => void;
  showExistingStatus?: boolean;
  debug?: boolean;
}

/**
 * Improved Boleto Button Component
 * 
 * This is an enhanced version of the original BoletoButton with:
 * - Better code organization and readability
 * - Clearer state management
 * - Improved error handling
 * - Better user feedback
 * - Confirmation dialog for existing boletos
 */
const BoletoButtonImproved: React.FC<BoletoButtonProps> = ({
  invoiceId,
  className = 'btn btn-warning',
  style,
  onBoletoCreated,
  onError,
  showExistingStatus = true,
  debug = false
}) => {
  // State management
  const [eligibility, setEligibility] = useState<BoletoEligibilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<BoletoExistsResponse | null>(null);

  /**
   * Check if boleto can be created for this invoice
   */
  const checkEligibility = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.checkBoletoEligibility(invoiceId);
      setEligibility(response);
    } catch (error: any) {
      console.error('Error checking boleto eligibility:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to check boleto eligibility';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, onError]);

  // Initialize component
  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  /**
   * Create boleto with optional force flag
   */
  const createBoleto = async (forceCreate: boolean = false) => {
    if (creating) return;
    setCreating(true);
    try {
      const response = await apiService.createBoletoForInvoice(invoiceId, undefined, forceCreate);
      
      if ((response as any)?.success) {
        handleSuccessfulCreation(response);
      } else if (isConfirmationRequired(response as any)) {
        handleConfirmationRequired(response as BoletoExistsResponse);
      } else {
        throw new Error((response as any)?.message || 'Failed to create boleto');
      }
    } catch (error: any) {
      handleCreationError(error);
    } finally {
      setCreating(false);
    }
  };

  /**
   * Handle successful boleto creation
   */
  const handleSuccessfulCreation = (response: any) => {
    alert('Boleto created successfully!');
    onBoletoCreated?.(response);
    
    // Close confirmation dialog if open
    closeConfirmationDialog();
    
    // Refresh eligibility to show new status
    checkEligibility();
  };

  /**
   * Handle case where confirmation is required
   */
  const handleConfirmationRequired = (response: BoletoExistsResponse) => {
    setConfirmationData(response);
    setShowConfirmation(true);
  };

  /**
   * Handle creation errors
   */
  const handleCreationError = (error: any) => {
    console.error('Error creating boleto:', error);
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to create boleto';
    alert(`Error: ${errorMessage}`);
    onError?.(errorMessage);
  };

  /**
   * Check if response requires confirmation
   */
  const isConfirmationRequired = (response: any): boolean => {
    return 'requires_confirmation' in response && response.requires_confirmation;
  };

  /**
   * Handle confirmation dialog actions
   */
  const handleConfirmCreate = () => {
    createBoleto(true); // Force create new boleto
  };

  const closeConfirmationDialog = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  /**
   * Get button configuration based on current state
   */
  const getButtonConfig = () => {
    if (!eligibility?.eligible) {
      const reason = !eligibility?.currency_is_brl ? 'Currency must be BRL' : 'Customer must have tax ID';
      return {
        text: '🇧🇷 Boleto N/A',
        className: `${className} disabled`,
        style: { ...style, opacity: 0.5 },
        disabled: true,
        title: `Boleto not available: ${reason}`,
        onClick: undefined
      };
    }

    if (eligibility?.existing_boleto) {
      return {
        text: '🇧🇷 Boleto Created',
        className: className.replace('btn-warning', 'btn-success'),
        style: style,
        disabled: true,
        title: `Boleto already exists (Status: ${eligibility.existing_boleto.status})`,
        onClick: undefined
      };
    }

    return {
      text: creating ? 'Creating...' : '🇧🇷 Create Boleto',
      className: className,
      style: style,
      disabled: creating,
      title: 'Create boleto payment for this BRL invoice',
      onClick: () => createBoleto(false)
    };
  };

  // Render loading state
  if (loading) {
    return (
      <button className={className} style={style} disabled>
        Checking...
      </button>
    );
  }

  const buttonConfig = getButtonConfig();

  return (
    <div className="boleto-button-container">
      <button
        className={buttonConfig.className}
        style={buttonConfig.style}
        onClick={buttonConfig.onClick}
        disabled={buttonConfig.disabled}
        title={buttonConfig.title}
      >
        {buttonConfig.text}
      </button>
      
      {/* Show existing boleto status if requested and available */}
      {showExistingStatus && eligibility?.existing_boleto && (
        <div className="mt-1">
          <small className="text-muted">
            Status: {eligibility.existing_boleto.status}
            {eligibility.existing_boleto.created_at && (
              <span> • {new Date(eligibility.existing_boleto.created_at).toLocaleDateString()}</span>
            )}
          </small>
        </div>
      )}
      
      {/* Confirmation Dialog */}
      {showConfirmation && confirmationData && (
        <BoletoConfirmationDialog
          isOpen={showConfirmation}
          boletoData={confirmationData}
          onConfirm={handleConfirmCreate}
          onCancel={closeConfirmationDialog}
          loading={creating}
        />
      )}
    </div>
  );
};

export default BoletoButtonImproved;